import * as React from 'react'

import { Box, Button, NoticeBox, Spinner, Text } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
// Hooks
import TextArea from 'modules/core/components/TextArea'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useFunLoading } from 'modules/ai/hooks/useFunLoading'
import useQuestion from 'modules/reports/hooks/useQuestion'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import reports from 'modules/reports/store/collections/reports'
import type Dashboard from 'modules/reports/store/models/Dashboard'

interface Props {
  dashboard: Dashboard
  onClose: () => void
  onSuccess: (reportId: string) => void
}

class QuestionForm extends FactorialForm {
  constructor(values: object) {
    super(values, {
      question: 'string',
    })
  }
}

const Loading: React.FC = () => {
  const loadingMessage = useFunLoading([
    i18n.t('custom_reports.question.modal.create.loading.engaging'),
    i18n.t('custom_reports.question.modal.create.loading.disentangling'),
    i18n.t('custom_reports.question.modal.create.loading.bootstrapping'),
    i18n.t('custom_reports.question.modal.create.loading.comparing'),
    i18n.t('custom_reports.question.modal.create.loading.mapping'),
    i18n.t('custom_reports.question.modal.create.loading.backpropagating'),
  ])

  return (
    <Box alignItems="center" paddingY="s16">
      <Box paddingBottom="s24">
        <Text.Normal>{loadingMessage}</Text.Normal>
      </Box>
      <Spinner />
    </Box>
  )
}

const NewQuestionModal = ({ dashboard, onClose, onSuccess }: Props) => {
  const { track } = useReportTracking()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { loading, error, updateQuestion } = useQuestion(companyId, dashboard.id)

  const [question, setQuestion] = React.useState<string>('')

  const handleSubmit = () => {
    if (question) {
      track(trackingEvents.reports.submitReportFromQuestion, {
        company_id: companyId,
      })
      updateQuestion(question).then(({ data, error }) => {
        if (error) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('custom_reports.question.modal.create.question.error'),
          })
        } else {
          const report = data.report
          if (report) {
            reports.fetch({ data: { dashboard_id: dashboard.id } }).then(() => {
              onSuccess(report.id)
            })
          }
        }
      })
    }
  }

  return (
    <Modal
      illustration="user-interaction-review_chart"
      title={i18n.t('custom_reports.question.modal.create.title')}
      description={i18n.t('custom_reports.question.modal.create.description')}
      onClose={onClose}
      onSubmit={handleSubmit}
      locked={loading}
      footer={
        <>
          <Button disabled={loading} type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading || question.length === 0} submit>
            {i18n.t('custom_reports.question.modal.create.question.ask')}
          </Button>
        </>
      }
    >
      <TextArea
        label={i18n.t('custom_reports.question.modal.create.question.label')}
        placeholder={i18n.t('custom_reports.question.modal.create.question.placeholder')}
        mandatory
        value={question}
        readonly={loading}
        onChange={(value) => setQuestion(value)}
      />
      {loading && <Loading />}
      {!loading && error && (
        <Box alignItems="center" paddingBottom="s16">
          <NoticeBox
            color="radicalRed200"
            message={i18n.t('custom_reports.question.modal.create.question.error')}
          />
        </Box>
      )}
    </Modal>
  )
}

export default observer(NewQuestionModal)
